import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import type { UserProfile } from '../../types'

// Org user administration (roles + status). Extracted from the retired
// AdminPanel; reads via the get_organization_users RPC so RLS stays the
// single enforcement point.
export function UserManagement() {
  const [users, setUsers] = useState<UserProfile[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const loadUsers = useCallback(async () => {
    try {
      const { data: orgUsers, error } = await supabase.rpc(
        'get_organization_users'
      )

      if (error) {
        console.error('Error loading users:', error)
        setError(error.message)
      } else {
        setError(null)
        setUsers((orgUsers || []) as unknown as UserProfile[])
      }
    } catch (err) {
      console.error('Error in loadUsers:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          role: newRole as
            | 'admin'
            | 'sales_user'
            | 'market_access_user'
            | 'super_admin',
        })
        .eq('id', userId)

      if (error) throw error
      await loadUsers()
    } catch (err) {
      console.error('Error updating user role:', err)
      alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  const updateUserStatus = async (userId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          status: newStatus as 'pending' | 'active' | 'inactive' | 'suspended',
        })
        .eq('id', userId)

      if (error) throw error
      await loadUsers()
    } catch (err) {
      console.error('Error updating user status:', err)
      alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Users</h2>
        <div className="text-center py-4">Loading users...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Users</h2>
        <div className="text-sm text-gray-600">
          {users.length} user{users.length !== 1 ? 's' : ''} in organization
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Joined
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map(user => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {user.first_name} {user.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={user.role}
                    onChange={e => updateUserRole(user.id, e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="market_access_user">Reviewer</option>
                    <option value="sales_user">Sales User</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={user.status}
                    onChange={e => updateUserStatus(user.id, e.target.value)}
                    className={`text-sm border rounded px-2 py-1 ${
                      user.status === 'active'
                        ? 'border-green-300 text-green-700 bg-green-50'
                        : user.status === 'pending'
                          ? 'border-yellow-300 text-yellow-700 bg-yellow-50'
                          : 'border-red-300 text-red-700 bg-red-50'
                    }`}
                  >
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No users found in this organization.
          </div>
        )}
      </div>
    </div>
  )
}
